import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  BrainCircuit, Target, TrendingUp, HelpCircle, Users, Video, LayoutDashboard,
  CircleAlert as AlertCircle, Sparkles, MessageSquare, BarChart3, ClipboardList,
} from 'lucide-react';
import { getOpportunity, MavisApiError, saveQualification, updateOpportunity } from '../../lib/mavisApi';
import { useAuth } from '../../contexts/AuthContext';
import type {
  MavisOpportunity,
  MavisOpportunityQualification,
  MavisStakeholder,
  MavisMeetingSession,
  MeddpiccData,
  NowToValueData,
  ThreeWhysData,
  MeddpiccKey,
  NowToValueKey,
} from '../../types/mavis';
import type { Hypothesis, UseCaseItem, AccountTrigger } from '../../types';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import MavisOpportunityHeader from '../../components/mavis/MavisOpportunityHeader';
import MavisLivePlaybook from '../../components/mavis/MavisLivePlaybook';
import MavisOverviewPanel from '../../components/mavis/MavisOverviewPanel';
import MavisQualificationScorecard from '../../components/mavis/MavisQualificationScorecard';
import MavisMeddpiccPanel from '../../components/mavis/MavisMeddpiccPanel';
import MavisNowToValuePanel from '../../components/mavis/MavisNowToValuePanel';
import MavisThreeWhysPanel from '../../components/mavis/MavisThreeWhysPanel';
import MavisStakeholdersPanel from '../../components/mavis/MavisStakeholdersPanel';
import type { ContactOption } from '../../components/mavis/MavisAddStakeholderModal';
import MavisMeetingPanel from '../../components/mavis/MavisMeetingPanel';
import MavisPrepBriefPanel from '../../components/mavis/MavisPrepBriefPanel';
import MavisDealCoachPanel from '../../components/mavis/MavisDealCoachPanel';
import MavisForecastAssessmentPanel from '../../components/mavis/MavisForecastAssessmentPanel';

// ── Types ──────────────────────────────────────────────────────────────────────

interface AccountSummary {
  id: string;
  company_name: string;
  logo_url: string | null;
  website_url: string | null;
  industry: string | null;
  tier: string | null;
}

type OpportunityDetailRow = MavisOpportunity & { accounts: AccountSummary | null };

// ── Defaults (match migration JSONB defaults) ──────────────────────────────────

function emptyMeddpicc(): MeddpiccData {
  const keys: MeddpiccKey[] = [
    'metrics', 'economic_buyer', 'decision_criteria', 'decision_process',
    'partners', 'paper_process', 'implicated_pain', 'champion', 'competition',
  ];
  return Object.fromEntries(
    keys.map(k => [k, { value: '', notes: '', score: 0 as const }]),
  ) as MeddpiccData;
}

function emptyNowToValue(): NowToValueData {
  const keys: NowToValueKey[] = [
    'current_state', 'negative_consequences', 'future_state',
    'positive_business_outcomes', 'required_capabilities',
    'timelines_and_milestones', 'personas_required',
  ];
  return Object.fromEntries(
    keys.map(k => [k, { value: '', notes: '' }]),
  ) as NowToValueData;
}

function emptyThreeWhys(): ThreeWhysData {
  return { why_change: '', why_now: '', why_us: '' };
}

// ── Constants ──────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview',        label: 'Overview',        icon: LayoutDashboard },
  { id: 'prep_brief',      label: 'MAVIS Brief',     icon: Sparkles },
  { id: 'deal_coach',      label: 'Deal Coach',      icon: MessageSquare },
  { id: 'assessment',      label: 'Assessment',      icon: BarChart3 },
  { id: 'qualification',   label: 'Qualification',   icon: ClipboardList },
  { id: 'stakeholders',    label: 'Stakeholders',    icon: Users },
  { id: 'meeting_control', label: 'Meeting Control', icon: Video },
];

const QUAL_SUBTABS = [
  { id: 'meddpicc',     label: 'MEDDPICC',      icon: Target },
  { id: 'now_to_value', label: 'Now to Value',   icon: TrendingUp },
  { id: 'three_whys',   label: '3 Whys',         icon: HelpCircle },
] as const;

type QualSubTab = typeof QUAL_SUBTABS[number]['id'];

// ── Placeholder ────────────────────────────────────────────────────────────────

function PlaceholderSection({ title, description }: { title: string; description: string }) {
  return (
    <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm">
      <div className="px-6 py-4 border-b border-app-line">
        <h3 className="text-base font-semibold text-app-ink">{title}</h3>
      </div>
      <div className="py-16 text-center px-6">
        <BrainCircuit className="w-10 h-10 text-app-muted mx-auto mb-4" />
        <p className="text-sm font-medium text-app-muted mb-1">{title}</p>
        <p className="text-xs text-app-muted max-w-sm mx-auto">{description}</p>
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function MavisOpportunityDetail() {
  const { opportunityId } = useParams<{ opportunityId: string }>();
  const navigate = useNavigate();
  const [searchParams]=useSearchParams();
  const { organisation } = useAuth();

  // Core opportunity data
  const [opportunity, setOpportunity] = useState<MavisOpportunity | null>(null);
  const [account, setAccount] = useState<AccountSummary | null>(null);

  // Qualification row (one-to-one with opportunity)
  const [qualification, setQualification] = useState<MavisOpportunityQualification | null>(null);

  // Stakeholders
  const [stakeholders, setStakeholders] = useState<MavisStakeholder[]>([]);
  const [contacts, setContacts] = useState<ContactOption[]>([]);

  // Meeting sessions
  const [meetingSessions, setMeetingSessions] = useState<MavisMeetingSession[]>([]);

  // Heymakr read-only context
  const [hypothesis, setHypothesis] = useState<Hypothesis | null>(null);
  const [useCases, setUseCases] = useState<UseCaseItem[]>([]);
  const [triggers, setTriggers] = useState<AccountTrigger[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.some(t=>t.id===searchParams.get('tab'))?searchParams.get('tab')!:'overview');
  const [qualSubTab, setQualSubTab] = useState<QualSubTab>('meddpicc');

  const loadData = useCallback(async () => {
    if (!opportunityId || !organisation) return;
    setLoading(true);
    setError(null);
    setNotFound(false);

    // Opportunity + account + qualification + stakeholders + contacts + meetings + Heymakr context
    // in a single request. Wrapped in try/finally so a network error never leaves loading stuck at true.
    try {
      let detail;
      try {
        detail = await getOpportunity(opportunityId);
      } catch (err) {
        if (err instanceof MavisApiError && err.status === 404) {
          setNotFound(true);
          return;
        }
        throw err;
      }

      const row = detail.opportunity as unknown as OpportunityDetailRow;
      setAccount(row.accounts);
      setOpportunity(row as unknown as MavisOpportunity);

      // Auto-create qualification row if it doesn't exist yet
      let qualRow = detail.qualification as unknown as MavisOpportunityQualification | null;
      if (!qualRow) {
        try {
          const { qualification: created } = await saveQualification(opportunityId, {
            meddpicc:     emptyMeddpicc(),
            now_to_value: emptyNowToValue(),
            three_whys:   emptyThreeWhys(),
          });
          qualRow = created as unknown as MavisOpportunityQualification;
        } catch {
          // Leave qualification empty; the panels show a "being prepared" placeholder.
        }
      }
      setQualification(qualRow);

      // Stakeholders + contacts + meetings
      setStakeholders(detail.stakeholders as unknown as MavisStakeholder[]);
      setContacts(detail.contacts as unknown as ContactOption[]);
      setMeetingSessions(detail.meetings as unknown as MavisMeetingSession[]);

      // Heymakr context
      if (detail.hypothesis) setHypothesis(detail.hypothesis as unknown as Hypothesis);
      if (detail.use_cases?.use_case_data) {
        const items = detail.use_cases.use_case_data as UseCaseItem[];
        setUseCases([...items].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0)));
      }
      setTriggers(detail.triggers as unknown as AccountTrigger[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load opportunity data');
    } finally {
      setLoading(false);
    }
  }, [opportunityId, organisation]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Save handlers ──────────────────────────────────────────────────────────

  // Patch mavis_opportunities (opportunity header fields)
  const onPatch = useCallback(async (patch: Record<string, unknown>) => {
    if (!opportunityId || !opportunity) return;
    setSaving(true);

    try {
      await updateOpportunity(opportunityId, patch);
      setOpportunity(prev =>
        prev ? ({ ...prev, ...patch } as unknown as MavisOpportunity) : prev,
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // Save failed: keep the current values (no saved indicator).
    } finally {
      setSaving(false);
    }
  }, [opportunityId, opportunity]);

  // Reload the records the live playbook can change (qualification, stakeholders, contacts)
  const refreshConnected = useCallback(async () => {
    if (!opportunityId || !organisation) return;
    const detail = await getOpportunity(opportunityId);
    if (detail.qualification) setQualification(detail.qualification as unknown as MavisOpportunityQualification);
    setStakeholders(detail.stakeholders as unknown as MavisStakeholder[]);
    setContacts(detail.contacts.map((r: any) => ({ ...r, name: [r.first_name, r.last_name].filter(Boolean).join(' ') })));
  }, [opportunityId, organisation]);

  // Patch mavis_opportunity_qualifications (qualification framework fields)
  const onSaveQualification = useCallback(async (patch: Record<string, unknown>) => {
    if (!opportunityId || !qualification) return;
    setSaving(true);

    for(const framework of ['meddpicc','bant','now_to_value'])if(patch[framework]){const prior=(qualification as any)[framework]??{};patch[framework]=Object.fromEntries(Object.entries(patch[framework] as any).map(([key,value]:any)=>[key,value.value!==prior[key]?.value || value.notes!==prior[key]?.notes?{...value,source:'manual'}:value]));}
    try {
      await saveQualification(opportunityId, patch);
      setQualification(prev =>
        prev ? ({ ...prev, ...patch } as unknown as MavisOpportunityQualification) : prev,
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // Save failed: keep the current values (no saved indicator).
    } finally {
      setSaving(false);
    }
  }, [opportunityId, qualification]);

  // Typed wrappers — each panel receives a strongly-typed callback
  const saveMeddpicc   = useCallback((d: MeddpiccData)    => onSaveQualification({ meddpicc: d }), [onSaveQualification]);
  const saveNowToValue = useCallback((d: NowToValueData)  => onSaveQualification({ now_to_value: d }), [onSaveQualification]);
  const saveThreeWhys  = useCallback((d: ThreeWhysData)   => onSaveQualification({ three_whys: d }), [onSaveQualification]);

  // Called by MavisApplySuggestionsPanel after a qualification field is written to DB
  const onQualificationApplied = useCallback((column: string, merged: Record<string, unknown>) => {
    setQualification(prev =>
      prev ? ({ ...prev, [column]: merged } as unknown as MavisOpportunityQualification) : prev,
    );
  }, []);

  // State-only update after MavisForecastAssessmentPanel writes forecast to DB itself.
  // Do NOT call onPatch here — that would trigger a redundant second DB write.
  const onForecastAssessmentApplied = useCallback((patch: Record<string, unknown>) => {
    setOpportunity(prev =>
      prev ? ({ ...prev, ...patch } as unknown as MavisOpportunity) : prev,
    );
  }, []);

  // Stakeholder optimistic updates (the modal / panel handle the actual DB writes)
  const onStakeholderAdded   = useCallback((s: MavisStakeholder) => setStakeholders(prev => [...prev, s]), []);
  const onStakeholderUpdated = useCallback((s: MavisStakeholder) => setStakeholders(prev => prev.map(p => p.id === s.id ? s : p)), []);
  const onStakeholderDeleted = useCallback((id: string) => setStakeholders(prev => prev.filter(p => p.id !== id)), []);

  // Meeting session optimistic updates (panel handles DB writes)
  const onMeetingAdded   = useCallback((s: MavisMeetingSession) => setMeetingSessions(prev => [s, ...prev]), []);
  const onMeetingUpdated = useCallback((s: MavisMeetingSession) => setMeetingSessions(prev => prev.map(p => p.id === s.id ? s : p)), []);

  // ── Loading / error / not found ─────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (notFound || !opportunity) {
    return (
      <div className="p-8 text-center">
        <BrainCircuit className="w-12 h-12 text-app-muted mx-auto mb-4" />
        <p className="text-base font-semibold text-app-ink mb-1">Opportunity not found</p>
        <p className="text-sm text-app-muted mb-6">
          This opportunity may have been deleted or you may not have access to it.
        </p>
        <button onClick={() => navigate('/app/mavis')} className="btn-primary">
          Back to MAVIS
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl max-w-lg mx-auto">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={loadData} className="text-sm text-red-600 hover:underline whitespace-nowrap">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen">
      {/* Sticky header */}
      <div className="bg-app-surface border-b border-app-line sticky top-0 z-30">
        <div className="px-8 pt-4 pb-0">
          <MavisOpportunityHeader
            opportunity={opportunity}
            account={account ? { id: account.id, company_name: account.company_name } : null}
            saving={saving}
            saved={saved}
            onPatch={onPatch}
          />

          <div className="flex gap-1">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-all ${
                  activeTab === tab.id
                    ? 'border-forest-600 text-forest-700 bg-forest-50/50'
                    : 'border-transparent text-app-muted hover:text-app-ink hover:border-app-line-strong'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div className="p-8 space-y-6">

        {activeTab === 'overview' && (
          <><MavisOverviewPanel
            opportunity={opportunity}
            account={account}
            hypothesis={hypothesis}
            useCases={useCases}
            triggers={triggers}
            saving={saving}
            onPatch={onPatch}
          /><MavisLivePlaybook opportunityId={opportunity.id} view="tasks"/></>
        )}

        {activeTab === 'prep_brief' && opportunity && (
          <MavisPrepBriefPanel
            opportunityId={opportunity.id}
            orgId={opportunity.org_id}
            accountId={opportunity.account_id}
            onForecastApplied={onPatch}
            onStakeholderAdded={onStakeholderAdded}
            onQualificationApplied={onQualificationApplied}
          />
        )}

        {activeTab === 'deal_coach' && opportunity && (
          <MavisDealCoachPanel opportunityId={opportunity.id} />
        )}

        {activeTab === 'assessment' && opportunity && (
          <MavisForecastAssessmentPanel
            opportunity={opportunity}
            qualification={qualification}
            onForecastApplied={onForecastAssessmentApplied}
          />
        )}

        {activeTab === 'qualification' && (
          <>
            <MavisLivePlaybook opportunityId={opportunity.id} view="sync" onChange={()=>void refreshConnected()}/><MavisQualificationScorecard qualification={qualification} />

            {/* Subtab segmented control */}
            <div className="bg-app-surface rounded-xl border border-app-line shadow-sm px-4 py-2.5 flex items-center gap-1">
              {QUAL_SUBTABS.map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setQualSubTab(sub.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                    qualSubTab === sub.id
                      ? 'bg-forest-600 text-app-surface shadow-sm'
                      : 'text-app-muted hover:text-app-ink hover:bg-app-canvas'
                  }`}
                >
                  <sub.icon className="w-3 h-3" />
                  {sub.label}
                </button>
              ))}
            </div>

            {/* Panel content — panels render their own card-per-field layout */}
            {qualSubTab === 'meddpicc' && (
              qualification ? (
                <MavisMeddpiccPanel
                  key={`meddpicc-${qualification.id}`}
                  data={qualification.meddpicc}
                  saving={saving}
                  onSave={saveMeddpicc}
                />
              ) : (
                <PlaceholderSection
                  title="MEDDPICC Qualification"
                  description="Qualification data is being prepared. Please refresh in a moment."
                />
              )
            )}
            {qualSubTab === 'now_to_value' && (
              qualification ? (
                <MavisNowToValuePanel
                  key={`ntv-${qualification.id}`}
                  data={qualification.now_to_value}
                  saving={saving}
                  onSave={saveNowToValue}
                />
              ) : (
                <PlaceholderSection
                  title="Now to Value"
                  description="Qualification data is being prepared. Please refresh in a moment."
                />
              )
            )}
            {qualSubTab === 'three_whys' && (
              qualification ? (
                <MavisThreeWhysPanel
                  key={`3whys-${qualification.id}`}
                  data={qualification.three_whys}
                  saving={saving}
                  onSave={saveThreeWhys}
                />
              ) : (
                <PlaceholderSection
                  title="3 Whys"
                  description="Qualification data is being prepared. Please refresh in a moment."
                />
              )
            )}
          </>
        )}

        {activeTab === 'stakeholders' && opportunity && (
          <><MavisLivePlaybook opportunityId={opportunity.id} view="stakeholders" onChange={()=>void refreshConnected()}/><MavisStakeholdersPanel
            stakeholders={stakeholders}
            contacts={contacts}
            accountId={opportunity.account_id}
            opportunityId={opportunity.id}
            orgId={opportunity.org_id}
            onStakeholderAdded={onStakeholderAdded}
            onStakeholderUpdated={onStakeholderUpdated}
            onStakeholderDeleted={onStakeholderDeleted}
          /></>
        )}

        {activeTab === 'meeting_control' && opportunity && (
          <MavisMeetingPanel
            sessions={meetingSessions}
            opportunityId={opportunity.id}
            accountId={opportunity.account_id}
            onSessionAdded={onMeetingAdded}
            onSessionUpdated={onMeetingUpdated}
          />
        )}
      </div>
    </div>
  );
}
